import React, { useState, useEffect } from "react";
import {
  useDeleteNetworkMutation,
  useGetNetworksQuery,
  useUpdateNetworkMutation,
} from "state/api";
import { useGetTrackingMethodsQuery } from "state/api";
import Loader from "components/Loader";
import { Edit, Trash2, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import getNetworkColumns from "./columns";
import NetworksAppBar from "../components/AppBar";
import TailwindTable from "../components/TailwindTable";
import NetworkStatsCards, { NetworkMethodBreakdown } from "../components/NetworkStatsCards";

export const Networks = () => {
  const { data: networks = [], isLoading: isLoadingNetworks } = useGetNetworksQuery();
  const { data: trackingMethods = [], isLoading: isLoadingMethods } = useGetTrackingMethodsQuery();
  const [updateNetwork] = useUpdateNetworkMutation();
  const [deleteNetwork] = useDeleteNetworkMutation();
  const navigate = useNavigate();

  // Estados
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNetworks, setFilteredNetworks] = useState([]);
  const [showDetailedStats, setShowDetailedStats] = useState(false);

  // Filtrar redes com base no termo de busca
  useEffect(() => {
    if (networks && networks.length > 0) {
      if (!searchTerm) {
        setFilteredNetworks(networks);
      } else {
        const lowercaseSearch = searchTerm.toLowerCase();
        const filtered = networks.filter(network => 
          network.name.toLowerCase().includes(lowercaseSearch) ||
          network.tracking_method.toLowerCase().includes(lowercaseSearch) ||
          network.template.toLowerCase().includes(lowercaseSearch)
        );
        setFilteredNetworks(filtered);
      }
    }
  }, [networks, searchTerm]);

  // Função para começar a edição
  const handleEdit = (network) => {
    setEditingId(network.id);
    setEditValues({
      name: network.name,
      tracking_method: network.tracking_method,
      template: network.template
    });
  };

  // Função para salvar a edição
  const handleSave = async () => {
    try {
      await updateNetwork({
        id: editingId,
        ...editValues
      }).unwrap();
      setEditingId(null);
    } catch (error) {
      console.error("Erro ao atualizar rede:", error);
    }
  };

  // Função para cancelar a edição
  const handleCancel = () => {
    setEditingId(null);
  };

  // Função para excluir uma rede
  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir esta rede?")) {
      try {
        await deleteNetwork(id).unwrap();
      } catch (error) {
        console.error("Erro ao excluir rede:", error);
      }
    }
  };

  // Função para lidar com mudanças nos inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Redirecionamento para página de criação
  const handleCreate = () => {
    navigate('/network/create');
  };

  // Componente para renderizar as ações de cada linha
  const renderRowActions = (row) => {
    if (editingId === row.id) {
      return (
        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 h-8 w-8 p-0"
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            onClick={handleCancel}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }
    
    return (
      <div className="flex space-x-2">
        <button
          onClick={() => handleEdit(row)}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 h-8 w-8 p-0"
        >
          <Edit className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleDelete(row.id)}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 h-8 w-8 p-0"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // Busca com filtragem explícita
  const handleSearch = () => {
    if (networks && networks.length > 0) {
      const lowercaseSearch = searchTerm.toLowerCase();
      const filtered = networks.filter(network => 
        network.name.toLowerCase().includes(lowercaseSearch) ||
        network.tracking_method.toLowerCase().includes(lowercaseSearch) ||
        network.template.toLowerCase().includes(lowercaseSearch)
      );
      setFilteredNetworks(filtered);
    }
  };

  if (isLoadingNetworks || isLoadingMethods) {
    return <Loader />;
  }

  // Obter as definições de colunas
  const columns = getNetworkColumns(trackingMethods, editingId !== null, editingId, editValues, handleChange);
  
  return (
    <div className="flex flex-col space-y-4 p-4 min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Cabeçalho com cards de estatísticas */}
      <div className="rounded-lg bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
              Gerenciamento de Redes
            </h1>
            <p className="text-blue-100">
              Visualize, edite e gerencie as redes de afiliados
            </p>
          </div>
          <button
            onClick={() => setShowDetailedStats(!showDetailedStats)}
            className="px-4 py-2 bg-white/20 backdrop-blur rounded-md text-white hover:bg-white/30 transition-colors text-sm"
          >
            {showDetailedStats ? 'Ocultar Detalhes' : 'Ver Detalhes'}
          </button>
        </div>
        
        {/* Cards de Estatísticas */}
        <NetworkStatsCards 
          networks={networks} 
          trackingMethods={trackingMethods} 
        />
        
        {/* Breakdown detalhado (opcional) */}
        {showDetailedStats && (
          <div className="mt-4">
            <NetworkMethodBreakdown 
              networks={networks} 
              trackingMethods={trackingMethods} 
            />
          </div>
        )}
      </div>

      {/* AppBar com ações */}
      <NetworksAppBar 
        handleCreate={handleCreate}
        handleSearch={handleSearch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {/* Tabela de dados */}
      <TailwindTable
        columns={columns}
        data={filteredNetworks}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={filteredNetworks.length}
        renderRowActions={renderRowActions}
        isLoading={isLoadingNetworks}
      />
    </div>
  );
};

export default React.memo(Networks);